import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAnonymousSessionUsage,
  setAnonymousSessionUsage,
  UsageData,
} from "helpers/api";
import { getDistinctId } from "helpers/services/mixpanel";
import { useFingerprint } from "hooks/useFingerprint";
import { useEffect, useState } from "react";

/**
 * Generates the query key for session usage caching
 */
export function getSessionUsageQueryKey(
  sessionId?: string,
  fingerprint?: string
) {
  return ["session_usage", sessionId, fingerprint];
}

function useSessionUsage() {
  const { isSignedIn, isLoaded } = useAuth();
  const isNotSignedIn = isLoaded && !isSignedIn;
  const queryClient = useQueryClient();
  const { fingerprint, isLoading: fingerprintLoading } = useFingerprint();

  // TODO (CON-2230 on 9/21/25) remove this once we've migrated to fingerprint
  const [currentSessionId, setCurrentSessionId] = useState<string | undefined>(
    undefined
  );

  // Check for distinctId periodically until mixpanel is initialized
  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkDistinctId = () => {
      const distinctId = getDistinctId();
      if (distinctId && distinctId !== currentSessionId) {
        setCurrentSessionId(distinctId);
      }
    };

    // Check immediately
    checkDistinctId();

    // If we don't have a distinctId yet, keep checking until mixpanel is initialized
    if (!currentSessionId) {
      const interval = setInterval(checkDistinctId, 100);
      return () => clearInterval(interval);
    }
  }, [currentSessionId]);

  const fingerprintAvailable = !!fingerprint && !fingerprintLoading;
  const USAGE_QUERY_KEY = getSessionUsageQueryKey(
    currentSessionId,
    fingerprint ?? undefined
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: async () => {
      if (!currentSessionId) {
        throw new Error("No session ID available");
      }
      if (!fingerprint) {
        throw new Error("No fingerprint available");
      }
      const usage = await getAnonymousSessionUsage(
        currentSessionId,
        fingerprint
      );
      return usage as UsageData;
    },
    enabled: isNotSignedIn && !!currentSessionId && fingerprintAvailable,
  });

  const { mutate: updateUsage, isLoading: isSettingUsage } = useMutation({
    mutationFn: async (usageData: Partial<UsageData>) => {
      if (!currentSessionId) {
        throw new Error("No session ID available");
      }
      if (!fingerprint) {
        throw new Error("No fingerprint available");
      }
      if (!data) {
        throw new Error("No usage data available");
      }
      return setAnonymousSessionUsage(currentSessionId, fingerprint, {
        ...data,
        ...usageData,
      });
    },
    onSuccess: (newData) => {
      // Update the cache with the new data
      queryClient.setQueryData(USAGE_QUERY_KEY, newData);
    },
  });

  return {
    usage: data,
    isLoading,
    isFetching,
    error,
    updateUsage,
    isSettingUsage,
  };
}

export default useSessionUsage;
